/**
 * The trains domain (backend `TALIA_DOMAIN=trains`): Italo's brand, what the spec's screens need computed (builders.ts,
 * menus.ts), the train-specific widgets, and the station map an answer can open.
 */
import type { WidgetDefinition } from '@platform/widgets/contract';
import type { AttractVideoDef, DomainPack } from '@platform/packs/contract';
import { TRAINS_ACTIONS, TRAINS_BUILDERS } from './builders';
import { filter, help, newSearch, quickSearch } from './menus';
import { isTrainsSession } from './session';
import { seatMap } from './widgets/SeatMap';
import { stationMap } from './widgets/station-map/StationMap';
import { trainResults } from './widgets/TrainResults';
import { tripSummary } from './widgets/TripSummary';
import { tripTicket } from './widgets/TripTicket';
import TRAINS_UI from './ui.json';

/**
 * What the kiosk plays while it waits (DomainPack.attract): Italo's video, as on the old kiosk. Its
 * last 5 s are the ".italo is magic" ending, cut by the crop: the loop skips them.
 */
const ATTRACT: AttractVideoDef = { video: '/static/ItaloIsMagic-crop-video.webm', trimEndS: 5 };

/** How long the station map stays open after the answer that opened it (the old kiosk: 15 s). */
const MAP_OPEN_MS = 15_000;

export const trainsPack: DomainPack = {
  id: 'trains',
  recognizes: isTrainsSession,
  // Italo's brand: red and bordeaux (the old kiosk's src/styles/brands/italo.css). Surfaces are the theme's.
  theme: {
    '--brand-accent': '#a6160a',
    '--brand-accent-strong': '#5a0e07',
  },
  attract: ATTRACT,
  // the avatar in Italo's lounge (src/app/stage.json)
  stage: { avatar: 'male_standing', background: 'italo_lounge' },
  // What each screen shows is the spec's (backend/backend/domains/trains/spec.yaml `ui:`), the steps too: the pack
  // computes what the spec names by role, the views, the bar's menus and the actions that send a draft.
  builders: TRAINS_BUILDERS,
  menus: { quick_search: quickSearch, new_search: newSearch, filter, help },
  actions: TRAINS_ACTIONS,
  ui: TRAINS_UI,
  widgets: [trainResults, seatMap, stationMap, tripSummary, tripTicket] as unknown as WidgetDefinition<never>[],
  defaultPreset: 'kiosk',
  accessibilityButton: 'set_accessibility', // spec.yaml buttons: set_accessibility {mode}
  overlayFor: (message) =>
    message.map ? { widget: 'station-map', props: { station: message.map.station, level: message.map.level, highlight: message.map.highlight }, closeAfterMs: MAP_OPEN_MS } : null,
};
